import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { WorkoutSplit } from '../../../models/training.models';
import { getElevationMeasurementUnit } from '../../../helpers/general.helpers';
import { palette } from '../../../theme';

export interface WorkoutSplitsTableProps {
  splits: WorkoutSplit[];
  selectedSplitNumber?: number | null;
  onSelectSplit?: (splitNumber: number | null) => void;
}

const formatDurationValue = (duration: WorkoutSplit['duration']): string => {
  if (duration == null || duration === '') {
    return '—';
  }

  let totalSeconds: number;

  if (typeof duration === 'string') {
    const durationPart = duration.includes('/') ? duration.split('/')[0].trim() : duration.trim();
    const parts = durationPart.split(':').map((part) => Number(part));

    if (!parts.length || parts.some((part) => Number.isNaN(part))) {
      return '—';
    }

    if (parts.length === 3) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      totalSeconds = parts[0] * 60 + parts[1];
    } else {
      return '—';
    }
  } else {
    if (duration <= 0) {
      return '—';
    }
    totalSeconds = Math.round(duration);
  }

  return formatElapsedSeconds(totalSeconds);
};

const formatElapsedSeconds = (totalSeconds: number | null | undefined): string => {
  if (totalSeconds == null || Number.isNaN(Number(totalSeconds)) || totalSeconds < 0) {
    return '—';
  }

  const seconds = Math.round(Number(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatHrValue = (hr: number | null | undefined): string => {
  if (hr == null) {
    return '—';
  }
  return String(Math.round(hr));
};

const formatSpeedValue = (speed: number | null | undefined): string => {
  if (speed == null) {
    return '—';
  }
  return Number(speed).toFixed(1);
};

const formatAscentValue = (elevationGain: number | null | undefined, unit: string): string => {
  if (elevationGain == null || Number.isNaN(Number(elevationGain))) {
    return '—';
  }
  const value = Math.round(Number(elevationGain));
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}${unit}`;
};

const headerSx = {
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  fontSize: 10,
  fontWeight: 600,
  color: palette.text.secondary,
};

const gridColumns = '36px 56px 48px 48px 64px 56px 56px 72px';

const WorkoutSplitsTable: FC<WorkoutSplitsTableProps> = ({ splits, selectedSplitNumber = null, onSelectSplit }) => {
  if (!splits.length) {
    return null;
  }

  const elevationUnit = getElevationMeasurementUnit();

  const handleRowClick = (splitNumber: number) => {
    if (!onSelectSplit) {
      return;
    }
    onSelectSplit(selectedSplitNumber === splitNumber ? null : splitNumber);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="caption"
        sx={{
          ...headerSx,
          display: 'block',
          mb: 1.5,
          fontSize: 11,
          letterSpacing: '0.1em',
        }}
      >
        Splits (1 KM)
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          columnGap: 1,
          alignItems: 'center',
          px: 1,
          mb: 1,
        }}
      >
        <Typography sx={headerSx}>KM</Typography>
        <Typography sx={headerSx}>Split</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'center' }}>Avg</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'center' }}>Peak</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'right' }}>Ascent</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'right' }}>Avg S</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'right' }}>Top S</Typography>
        <Typography sx={{ ...headerSx, textAlign: 'right' }}>Elapsed</Typography>
      </Box>

      {splits.map((split) => {
        const isSelected = selectedSplitNumber === split.splitNumber;

        return (
          <Box
            key={split.splitNumber}
            role={onSelectSplit ? 'button' : undefined}
            tabIndex={onSelectSplit ? 0 : undefined}
            onClick={() => handleRowClick(split.splitNumber)}
            onKeyDown={(event) => {
              if (!onSelectSplit) {
                return;
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleRowClick(split.splitNumber);
              }
            }}
            sx={{
              display: 'grid',
              gridTemplateColumns: gridColumns,
              columnGap: 1,
              alignItems: 'center',
              py: 0.75,
              px: 1,
              borderRadius: 1.5,
              cursor: onSelectSplit ? 'pointer' : 'default',
              backgroundColor: isSelected ? palette.info.light : 'transparent',
              '&:hover': onSelectSplit
                ? { backgroundColor: isSelected ? palette.info.light : palette.background.alternate }
                : undefined,
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ color: palette.primary.main }}>
              {split.splitNumber}
            </Typography>

            <Typography variant="body2" sx={{ color: palette.text.primary }}>
              {formatDurationValue(split.duration)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'center', color: palette.text.primary }}>
              {formatHrValue(split.avgHr)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'center', color: palette.text.primary }}>
              {formatHrValue(split.topHr)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.text.secondary }}>
              {formatAscentValue(split.elevationGain, elevationUnit)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.primary.main, fontWeight: 600 }}>
              {formatSpeedValue(split.avgSpeed)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.purple.main, fontWeight: 600 }}>
              {formatSpeedValue(split.topSpeed)}
            </Typography>

            <Typography variant="body2" sx={{ textAlign: 'right', color: palette.text.primary, fontWeight: 700 }}>
              {formatElapsedSeconds(split.totalElapsedTime)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default WorkoutSplitsTable;
